<script lang="ts">
	import { goto } from '$app/navigation';
	import { PUBLIC_SMALL_PRODUCT_IMAGE } from '$env/static/public';
	import { cart } from '$lib/stores';
	import { currencyFormat } from '$lib/utils/formatters';
	import { Avatar, drawerStore } from '@skeletonlabs/skeleton';
	import { XMark } from '@svg';
	import { slide } from 'svelte/transition';

	export let shipping: number | null = null,
		hasTestsProduct: boolean = false;

	const onCheckout = !!shipping || shipping === 0;

	const removeItem = (id: number) => {
		cart.update((previousData) => {
			const index = previousData.findIndex((pd) => pd.id === id);
			if (index !== -1) previousData.splice(index, 1);
			return previousData;
		});
	};

	const increaseAmount = (id: number) => {
		cart.update((previousData) => {
			const index = previousData.findIndex((pd) => pd.id === id);
			if (index !== -1) previousData[index].amount++;

			return previousData;
		});
	};

	const decreaseAmount = (id: number) => {
		cart.update((previousData) => {
			const index = previousData.findIndex((pd) => pd.id === id);
			if (index !== -1) previousData[index].amount--;

			return previousData;
		});
	};
</script>

<div class="flex flex-col relative xsm:p-6 {!onCheckout && 'p-6'}">
	<h3 class="h3 mb-4">{!onCheckout ? 'Carrito de compras' : 'Resumen del pedido'}</h3>

	{#if !onCheckout}
		<button class="absolute top-6 right-5 text-surface-500 hover:text-surface-800" on:click={drawerStore.close}>
			<XMark class="w-8" />
		</button>
	{/if}

	<div class="divide-y divide-surface-300 mb-10 {!onCheckout && 'overflow-y-auto max-h-[63vh] sm:max-h-[61vh]'}">
		{#each $cart as { id, name, amount, image_url_0, price, discount }}
			<div class="flex space-x-4 py-4" transition:slide|local>
				<Avatar
					src={image_url_0 ? `${PUBLIC_SMALL_PRODUCT_IMAGE}${image_url_0}` : undefined}
					rounded="rounded-2xl"
					initials={name}
					background="bg-transparent"
				/>

				<div class="flex-1 flex flex-col justify-between">
					<span class="font-medium leading-tight">{name}</span>

					<div class="flex space-x-2">
						<p>Cantidad</p>
						<div>
							<button
								class="btn-styless px-2 variant-soft-surface rounded-lg"
								on:click={() => (amount > 1 ? decreaseAmount(id) : removeItem(id))}
							>
								-
							</button>
							{amount}
							<button class="btn-styless px-2 variant-soft-surface rounded-lg" on:click={() => increaseAmount(id)}>
								+
							</button>
						</div>
					</div>
				</div>

				<div class="flex flex-col text-right justify-between">
					<div class="flex flex-col-reverse sm:flex-row sm:space-x-1">
						<span class={discount ? 'line-through opacity-70 text-sm' : ''}>
							{currencyFormat(price ?? 0)}
						</span>
						{#if discount && price}
							<span class="text-primary-500">
								{currencyFormat(price - price * (discount / 100))}
							</span>
						{/if}
					</div>

					<button class="text-surface-500 self-end" on:click={() => removeItem(id)}>Eliminar</button>
				</div>
			</div>
		{:else}
			<p class="text-lg">Aún no has agregado productos a tu carrito</p>
		{/each}
	</div>

	<hr class="my-4 absolute left-0 w-full {!onCheckout ? 'bottom-44' : 'bottom-40'}" />

	<div class="flex justify-between {onCheckout && 'mt-3'}">
		<p>Subtotal</p>
		<span class={!onCheckout ? 'font-medium' : ''}>
			{currencyFormat(
				$cart.reduce((accumulator, { price, amount, discount }) => {
					if (discount) {
						const priceWithDiscount = price - price * (discount / 100);
						return accumulator + priceWithDiscount * amount;
					} else {
						return accumulator + price * amount;
					}
				}, 0)
			)}
		</span>
	</div>

	{#if shipping !== null}
		<div class="flex justify-between mt-4">
			<p>Envío</p>
			{#if hasTestsProduct}
				<small>Sin costo de envío para productos de prueba</small>
			{:else}
				<span>{currencyFormat(shipping)}</span>
			{/if}
		</div>

		<hr class="my-4" />

		<div class="flex justify-between font-semibold">
			<p>Total</p>
			<span>
				{currencyFormat(
					$cart.reduce((accumulator, { price, amount, discount }) => {
						if (discount) {
							const priceWithDiscount = price - price * (discount / 100);
							return accumulator + priceWithDiscount * amount;
						} else {
							return accumulator + price * amount;
						}
					}, shipping ?? 0)
				)}
			</span>
		</div>
	{/if}

	{#if !onCheckout}
		<small class="">El costo de envío se calcula en el siguiente paso.</small>

		<button
			class="btn variant-ghost mt-6 {$cart.length === 0 && 'pointer-events-none opacity-50'}"
			on:click={() => (goto('/checkout'), drawerStore.close())}
		>
			Proceder al pago
		</button>
		<small class="mt-2 mx-auto">
			o <button on:click={drawerStore.close} class="text-surface-500">continua comprando</button>
		</small>
	{/if}
</div>
